import { Fragment, createElement, type ReactNode } from 'react';
import { looksLikeHtml, sanitizeRichText } from '@trellis/shared';
import { Markdown } from './Markdown';

/**
 * Lo que el paciente lee: HTML del editor, o Markdown del contenido viejo.
 *
 * **Nunca `dangerouslySetInnerHTML`.** El HTML se convierte en elementos de
 * React, uno por uno, con la misma lista blanca que usa el saneador. Es la
 * misma decisión que ya tenía el renderizador de Markdown y por el mismo
 * motivo: los textos los escribe la institución desde una consola, y lo peor
 * que puede pasar acá es que una etiqueta rara no se dibuje — no que se ejecute
 * en el navegador de todos los pacientes de un programa.
 *
 * Los contenidos cargados antes del editor están en Markdown y siguen
 * leyéndose: migrarlos en silencio sería reescribir textos que alguien redactó.
 */
export function RichTextView({ source, className }: { source: string; className?: string }) {
  if (!source?.trim()) return null;
  if (!looksLikeHtml(source)) return <Markdown source={source} className={className} />;

  return <div className={className}>{parsear(sanitizeRichText(source))}</div>;
}

/** Cómo se dibuja cada etiqueta. Lo que no está acá, no se dibuja. */
const CLASES: Record<string, string> = {
  h2: 'text-xl font-semibold mt-6 mb-2 tracking-tight',
  h3: 'text-lg font-semibold mt-5 mb-1.5',
  h4: 'font-semibold mt-4 mb-1',
  p: 'mb-3 leading-relaxed',
  ul: 'list-disc pl-5 mb-3 space-y-1',
  ol: 'list-decimal pl-5 mb-3 space-y-1',
  li: '',
  blockquote: 'border-l-2 border-border pl-3 my-3 text-fg-muted',
  strong: 'font-semibold',
  b: 'font-semibold',
  em: 'italic',
  i: 'italic',
  u: 'underline',
  s: 'line-through',
  code: 'font-mono text-[0.9em] bg-surface px-1 py-0.5 rounded',
  pre: 'font-mono text-sm bg-surface p-3 rounded-md overflow-x-auto mb-3',
  a: 'text-primary underline',
  hr: 'border-t border-border my-5',
  br: '',
  span: '',
};

const VOID_TAGS = new Set(['br', 'hr']);

interface Nodo {
  tag: string;
  href?: string;
  /** Solo color y tamaño, y ya verificados por el saneador. */
  style?: Record<string, string>;
  hijos: Array<Nodo | string>;
}

/**
 * HTML ya saneado → elementos de React.
 *
 * Se asume la salida del saneador, que garantiza etiquetas conocidas, cerradas
 * y balanceadas. Aun así se ignora lo que no reconoce en vez de confiar: esto
 * también recibe contenido guardado antes de que el saneador existiera.
 */
function parsear(html: string): ReactNode[] {
  const raiz: Nodo = { tag: 'root', hijos: [] };
  const pila: Nodo[] = [raiz];

  const TAG = /<\/?\s*([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)\/?>/;
  let resto = html;

  for (;;) {
    const match = TAG.exec(resto);
    const actual = pila[pila.length - 1]!;

    if (!match) {
      if (resto) actual.hijos.push(desescapar(resto));
      break;
    }

    if (match.index > 0) actual.hijos.push(desescapar(resto.slice(0, match.index)));

    const [entera, nombreCrudo, atributos] = match;
    const tag = nombreCrudo!.toLowerCase();
    const cierre = entera.startsWith('</');
    resto = resto.slice(match.index + entera.length);

    if (!(tag in CLASES)) continue;

    if (cierre) {
      // Solo se cierra si es lo que está abierto: un cierre desparejo no puede
      // desarmar la pila y llevarse el resto del artículo adentro de un nodo.
      if (pila.length > 1 && actual.tag === tag) pila.pop();
      continue;
    }

    const nodo: Nodo = { tag, hijos: [] };

    if (tag === 'span') {
      const crudo = /style\s*=\s*"([^"]*)"/.exec(atributos ?? '')?.[1];
      if (crudo) {
        const style: Record<string, string> = {};
        for (const parte of desescapar(crudo).split(';')) {
          const [propiedad, ...resto] = parte.split(':');
          if (!propiedad || resto.length === 0) continue;
          const nombre = propiedad.trim();
          // Se pasa a la forma que espera React. Son dos y están cerradas por
          // el saneador, así que no hace falta un conversor general.
          if (nombre === 'color') style.color = resto.join(':').trim();
          if (nombre === 'font-size') style.fontSize = resto.join(':').trim();
        }
        if (Object.keys(style).length > 0) nodo.style = style;
      }
    }

    if (tag === 'a') {
      const href = /href\s*=\s*"([^"]*)"/.exec(atributos ?? '')?.[1];
      if (href) nodo.href = desescapar(href);
    }

    actual.hijos.push(nodo);
    if (!VOID_TAGS.has(tag)) pila.push(nodo);
  }

  return raiz.hijos.map((hijo, indice) => dibujar(hijo, indice));
}

function dibujar(nodo: Nodo | string, key: number): ReactNode {
  if (typeof nodo === 'string') return <Fragment key={key}>{nodo}</Fragment>;

  const hijos = nodo.hijos.map((hijo, indice) => dibujar(hijo, indice));
  const className = CLASES[nodo.tag] || undefined;

  if (VOID_TAGS.has(nodo.tag)) return createElement(nodo.tag, { key, className });

  if (nodo.tag === 'a') {
    return (
      <a
        key={key}
        href={nodo.href}
        className={className}
        // Un enlace del contenido lleva afuera de la app: `noreferrer` para no
        // filtrar de qué programa salió el paciente.
        target="_blank"
        rel="noopener noreferrer"
      >
        {hijos}
      </a>
    );
  }

  return createElement(nodo.tag, { key, className, style: nodo.style }, hijos);
}

function desescapar(texto: string): string {
  return texto
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}
